import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import {
  theme,
  Card,
  Typography,
  Button,
  Space,
  Tabs,
  Table,
  Tag,
  Switch,
  Tooltip,
  Statistic,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Badge,
  App,
  Drawer,
  Descriptions,
  Divider,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  BellOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExperimentOutlined,
  UserOutlined,
  MobileOutlined,
  MailOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  DeleteOutlined,
  AppleOutlined,
  AndroidOutlined,
} from '@ant-design/icons';
import {
  NotificationType,
  NotificationEventType,
  NotificationChannel,
  NotificationLog,
  UserPushPermissionSummary,
  UserPushTokenDevice,
} from '@mygate/common';
import { notificationsService } from '@/services/notifications';
import { formatDateTime } from '@/utils/date';
import { noDevicesDescription } from '@/utils/push-permission';
import { NotificationTypeEditorModal } from '@/components/NotificationTypeEditorModal';
import { NotificationLaboratory } from '@/components/NotificationLaboratory';
import { UserDetailModal } from '@/components/UserDetailModal';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Queue Status Badge Component
function QueueStatusBadge({ pending, processing, failed }: { pending: number; processing: number; failed: number }) {
  return (
    <Space wrap size="middle">
      <Badge count={pending} showZero color="blue">
        <Tag icon={<ClockCircleOutlined />}>Pending</Tag>
      </Badge>
      <Badge count={processing} showZero color="cyan">
        <Tag icon={<SyncOutlined spin={processing > 0} />}>Processing</Tag>
      </Badge>
      <Badge count={failed} showZero color="red">
        <Tag icon={<CloseCircleOutlined />}>Failed</Tag>
      </Badge>
    </Space>
  );
}

// Helper to get channel tags from ChannelConfig
function getEnabledChannels(channels: NotificationType['channels']): NotificationChannel[] {
  const result: NotificationChannel[] = [];
  if (channels.primary) result.push(channels.primary);
  if (channels.fallbackChain) {
    channels.fallbackChain.forEach(ch => {
      if (!result.includes(ch)) result.push(ch);
    });
  }
  return result;
}

export function NotificationsPage() {
  const { token } = theme.useToken();
  const { message } = App.useApp();

  // Types tab state
  const [types, setTypes] = useState<NotificationType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [editingType, setEditingType] = useState<NotificationType | null>(null);
  const [editorVisible, setEditorVisible] = useState(false);

  // Analytics tab state
  const [analytics, setAnalytics] = useState<{
    totalSent: number;
    totalDelivered: number;
    totalFailed: number;
    byType: Record<string, { sent: number; delivered: number; failed: number }>;
    byChannel: Record<string, { sent: number; delivered: number; failed: number }>;
    dailyStats: Array<{ date: string; sent: number; delivered: number; failed: number }>;
  } | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Logs tab state
  const [logs, setLogs] = useState<Array<Partial<NotificationLog>>>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [logsFilters, setLogsFilters] = useState<{
    userId?: string;
    typeCode?: string;
    status?: string;
  }>({});

  // Log detail drawer state
  const [selectedLog, setSelectedLog] = useState<Partial<NotificationLog> | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pushTokens, setPushTokens] = useState<UserPushTokenDevice[]>([]);
  const [pushPermission, setPushPermission] = useState<UserPushPermissionSummary | null>(null);
  const [pushTokensLoading, setPushTokensLoading] = useState(false);

  // Queue status state
  const [queueStatus, setQueueStatus] = useState({ pending: 0, processing: 0, failed: 0 });

  // Track whether logs tab has been loaded at least once
  const logsInitializedRef = useRef(false);

  // UserDetailModal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setDetailModalVisible(true);
  };

  // Open log detail drawer
  const handleLogClick = useCallback(async (log: Partial<NotificationLog>) => {
    setSelectedLog(log);
    setDrawerOpen(true);
    setPushTokens([]);
    setPushPermission(null);

    if (log.userId) {
      setPushTokensLoading(true);
      try {
        const response = await notificationsService.getUserPushTokens(log.userId);
        if (response.success && response.data) {
          setPushTokens(response.data.devices);
          setPushPermission(response.data.permission);
        }
      } catch {
        console.error('Failed to fetch push tokens');
      } finally {
        setPushTokensLoading(false);
      }
    }
  }, []);

  // Fetch notification types
  const fetchTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const response = await notificationsService.getNotificationTypes();
      if (response.success && response.data) {
        setTypes(response.data.types as NotificationType[]);
      }
    } catch (error) {
      message.error('Failed to fetch notification types');
      console.error('Error fetching types:', error);
    } finally {
      setTypesLoading(false);
    }
  }, [message]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const params: { startDate?: string; endDate?: string } = {};
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await notificationsService.getAnalytics(params);
      if (response.success && response.data) {
        setAnalytics(response.data.stats);
      }
    } catch (error) {
      message.error('Failed to fetch analytics');
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [dateRange, message]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const response = await notificationsService.getLogs({
        ...logsFilters,
        page: logsPagination.page,
        limit: logsPagination.limit,
      });
      if (response.success && response.data) {
        setLogs(response.data.logs);
        setLogsPagination(prev => ({ ...prev, total: response.data?.total || 0 }));
      }
    } catch (error) {
      message.error('Failed to fetch logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [logsFilters, logsPagination.page, logsPagination.limit, message]);

  // Fetch queue status
  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await notificationsService.getQueueStatus();
      if (response.success && response.data) {
        setQueueStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching queue status:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTypes();
    fetchQueueStatus();
  }, [fetchTypes, fetchQueueStatus]);

  // Auto-refetch logs when pagination changes (only after initial load)
  useEffect(() => {
    if (logsInitializedRef.current) {
      fetchLogs();
    }
  }, [logsPagination.page, logsPagination.limit]);

  // Toggle notification type enabled status
  const handleToggleType = async (code: string, enabled: boolean) => {
    try {
      const response = await notificationsService.toggleNotificationType(code, enabled);
      if (response.success) {
        message.success(`Notification type ${enabled ? 'enabled' : 'disabled'}`);
        fetchTypes();
      }
    } catch (error) {
      message.error('Failed to toggle notification type');
      console.error('Error toggling type:', error);
    }
  };

  // Edit notification type
  const handleEditType = (type: NotificationType) => {
    setEditingType(type);
    setEditorVisible(true);
  };

  // Types table columns
  const typesColumns: ColumnsType<NotificationType> = [
    {
      title: 'Name',
      key: 'name',
      width: 300,
      render: (_, record) => (
        <div className="table-cell-stack notification-type-summary">
          <Text strong>{record.name}</Text>
          <Space wrap size={[6, 4]}>
            <Text className="table-cell-truncate table-cell-muted" title={record.code} code>
              {record.code}
            </Text>
            <Tag
              title={record.category}
              color={
                record.category === 'TRANSACTIONAL' ? 'blue' : record.category === 'PROMOTIONAL' ? 'green' : 'orange'
              }
            >
              {record.category}
            </Tag>
          </Space>
          {record.description && (
            <Typography.Paragraph className="table-cell-description" type="secondary" ellipsis={{ rows: 2 }}>
              {record.description}
            </Typography.Paragraph>
          )}
        </div>
      ),
    },
    {
      title: 'Delivery Chain',
      key: 'channels',
      width: 180,
      render: (_, record) => {
        const channels = getEnabledChannels(record.channels);
        const colorMap: Record<string, string> = {
          [NotificationChannel.PUSH]: 'purple',
          [NotificationChannel.EMAIL]: 'cyan',
          [NotificationChannel.SMS]: 'orange',
          [NotificationChannel.WHATSAPP]: 'green',
          [NotificationChannel.IN_APP_BANNER]: 'magenta',
        };
        return (
          <Space wrap size={2}>
            {channels.map((ch, i) => (
              <Fragment key={ch}>
                {i > 0 && <Text type="secondary">→</Text>}
                <Tag color={colorMap[ch] || 'default'} style={{ marginInlineEnd: 0 }}>
                  {ch}
                </Tag>
              </Fragment>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (enabled, record) => {
        const canEnable = (record as NotificationType & { canEnable?: boolean }).canEnable;
        const isDisabled = canEnable === false;
        const switchEl = (
          <Switch
            checked={enabled}
            onChange={checked => handleToggleType(record.code, checked)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            disabled={isDisabled}
          />
        );
        return isDisabled ? (
          <Tooltip title="This notification type cannot be enabled yet">{switchEl}</Tooltip>
        ) : (
          switchEl
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEditType(record)}>
          Edit
        </Button>
      ),
    },
  ];

  // Logs table columns
  const logsColumns: ColumnsType<Partial<NotificationLog>> = [
    {
      title: 'Zeitverlauf',
      key: 'timestamps',
      width: 200,
      render: (_, record) => {
        const deliveredAt = record.channelAttempts?.find(a => a.status === 'SUCCESS')?.deliveredAt;
        return (
          <div className="table-cell-stack">
            <Button
              type="link"
              className="table-cell-link"
              onClick={event => {
                event.stopPropagation();
                handleLogClick(record);
              }}
            >
              {record.createdAt ? formatDateTime(new Date(record.createdAt).toISOString()) : 'Details öffnen'}
            </Button>
            <Text className="table-cell-muted">
              Zugestellt: {deliveredAt ? formatDateTime(new Date(deliveredAt).toISOString()) : '—'}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (userId: string, record) => {
        if (!record.user) return userId ? <Text copyable={{ text: userId }}>{userId.slice(0, 12)}...</Text> : '-';
        const name = `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim();
        const displayName = name || record.user.phoneNumber || record.user.email || userId;
        return (
          <Button type="link" onClick={() => handleViewDetails(userId)} style={{ padding: 0, height: 'auto' }}>
            <Space wrap>
              <UserOutlined />
              <Text strong style={{ color: token.colorPrimary }}>
                {displayName}
              </Text>
            </Space>
          </Button>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'notificationTypeCode',
      key: 'notificationTypeCode',
      width: 200,
      render: code => (
        <Text className="table-cell-truncate" title={code} code>
          {code}
        </Text>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'finalChannel',
      key: 'finalChannel',
      width: 150,
      render: channel => {
        const colors: Record<string, string> = {
          [NotificationChannel.PUSH]: 'purple',
          [NotificationChannel.EMAIL]: 'cyan',
          [NotificationChannel.SMS]: 'orange',
          [NotificationChannel.WHATSAPP]: 'green',
          [NotificationChannel.IN_APP_BANNER]: 'magenta',
        };
        return (
          <Tag title={channel || 'N/A'} color={colors[channel || ''] || 'default'}>
            {channel || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'finalStatus',
      key: 'finalStatus',
      width: 140,
      render: status => {
        const colors: Record<string, string> = {
          DELIVERED: 'green',
          FAILED: 'red',
          CANCELLED: 'orange',
          OPTED_OUT: 'default',
        };
        return (
          <Tag title={status} color={colors[status || ''] || 'default'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Error',
      key: 'error',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        if (record.finalStatus !== 'FAILED') return '-';
        const failedAttempts = record.channelAttempts?.filter(a => a.status === 'FAILED') || [];
        const lastFailed = failedAttempts[failedAttempts.length - 1];
        const errorMsg = lastFailed?.error || record.error;
        return errorMsg ? (
          <Tooltip title={errorMsg}>
            <Text type="danger">{errorMsg}</Text>
          </Tooltip>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Attempts',
      dataIndex: 'channelAttempts',
      key: 'channelAttempts',
      width: 190,
      render: attempts => (
        <Space wrap>
          {(attempts || []).map((a: { channel: string; status: string }, i: number) => (
            <Tag key={i} color={a.status === 'SUCCESS' ? 'green' : a.status === 'FAILED' ? 'red' : 'default'}>
              {a.channel}: {a.status}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  // Types tab content
  const TypesTab = (
    <Card>
      <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'flex-end' }}>
        <Button icon={<ReloadOutlined />} onClick={fetchTypes} loading={typesLoading}>
          Refresh
        </Button>
      </Space>
      <Table
        columns={typesColumns}
        dataSource={types}
        rowKey="code"
        loading={typesLoading}
        pagination={false}
        tableLayout="fixed"
        expandable={{
          columnWidth: 40,
          rowExpandable: record => !!record.description,
          expandedRowRender: record => (
            <div className="table-expanded-description">
              <Text strong>{record.name} · Description</Text>
              <Typography.Paragraph>{record.description}</Typography.Paragraph>
            </div>
          ),
        }}
        scroll={{ x: 710 }}
      />
    </Card>
  );

  // Compute delivery rate
  const deliveryRate = analytics
    ? analytics.totalSent > 0
      ? (analytics.totalDelivered / analytics.totalSent) * 100
      : 0
    : 0;

  // Analytics tab content
  const AnalyticsTab = (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <RangePicker
              value={dateRange}
              onChange={dates => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            />
            <Button type="primary" onClick={fetchAnalytics} loading={analyticsLoading}>
              Apply Filter
            </Button>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchAnalytics} loading={analyticsLoading}>
            Refresh
          </Button>
        </Space>
      </Card>

      {analytics && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic title="Total Sent" value={analytics.totalSent} prefix={<BellOutlined />} />
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Total Delivered"
                  value={analytics.totalDelivered}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Total Failed"
                  value={analytics.totalFailed}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Delivery Rate"
                  value={deliveryRate}
                  precision={2}
                  suffix="%"
                  valueStyle={{ color: deliveryRate >= 90 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="By Type">
                <Table
                  scroll={{ x: 'max-content' }}
                  dataSource={Object.entries(analytics.byType).map(([type, stats]) => ({
                    type,
                    ...stats,
                  }))}
                  columns={[
                    { title: 'Type', dataIndex: 'type', key: 'type' },
                    { title: 'Sent', dataIndex: 'sent', key: 'sent', align: 'right' },
                    { title: 'Delivered', dataIndex: 'delivered', key: 'delivered', align: 'right' },
                    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' },
                  ]}
                  rowKey="type"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="By Channel">
                <Table
                  scroll={{ x: 'max-content' }}
                  dataSource={Object.entries(analytics.byChannel).map(([channel, stats]) => ({
                    channel,
                    ...stats,
                  }))}
                  columns={[
                    { title: 'Channel', dataIndex: 'channel', key: 'channel' },
                    { title: 'Sent', dataIndex: 'sent', key: 'sent', align: 'right' },
                    { title: 'Delivered', dataIndex: 'delivered', key: 'delivered', align: 'right' },
                    { title: 'Failed', dataIndex: 'failed', key: 'failed', align: 'right' },
                  ]}
                  rowKey="channel"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  // Logs tab content
  const LogsTab = (
    <Card>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Input
            placeholder="User ID"
            value={logsFilters.userId}
            onChange={e => setLogsFilters(prev => ({ ...prev, userId: e.target.value }))}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Type"
            value={logsFilters.typeCode}
            onChange={val => setLogsFilters(prev => ({ ...prev, typeCode: val }))}
            style={{ width: 200 }}
            allowClear
          >
            {Object.values(NotificationEventType).map(type => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Status"
            value={logsFilters.status}
            onChange={val => setLogsFilters(prev => ({ ...prev, status: val }))}
            style={{ width: 120 }}
            allowClear
          >
            <Select.Option value="DELIVERED">Delivered</Select.Option>
            <Select.Option value="FAILED">Failed</Select.Option>
            <Select.Option value="CANCELLED">Cancelled</Select.Option>
            <Select.Option value="OPTED_OUT">Opted Out</Select.Option>
          </Select>
          <Button
            type="primary"
            onClick={() => {
              if (logsPagination.page !== 1) {
                setLogsPagination(prev => ({ ...prev, page: 1 }));
                // useEffect will trigger fetchLogs when page changes
              } else {
                fetchLogs();
              }
            }}
          >
            Search
          </Button>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={fetchLogs} loading={logsLoading}>
          Refresh
        </Button>
      </Space>
      <Table
        columns={logsColumns}
        dataSource={logs}
        rowKey="id"
        loading={logsLoading}
        onRow={record => ({
          onClick: () => handleLogClick(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: logsPagination.page,
          pageSize: logsPagination.limit,
          total: logsPagination.total,
          onChange: (page, pageSize) => setLogsPagination(prev => ({ ...prev, page, limit: pageSize })),
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
        }}
        tableLayout="fixed"
        scroll={{ x: 1260 }}
      />
    </Card>
  );

  // Tab items
  const tabItems = [
    {
      key: 'types',
      label: (
        <span>
          <BellOutlined />
          Notification Types
        </span>
      ),
      children: TypesTab,
    },
    {
      key: 'analytics',
      label: (
        <span>
          <BarChartOutlined />
          Analytics
        </span>
      ),
      children: AnalyticsTab,
    },
    {
      key: 'logs',
      label: (
        <span>
          <UnorderedListOutlined />
          Logs
        </span>
      ),
      children: LogsTab,
    },
    {
      key: 'laboratory',
      label: (
        <span>
          <ExperimentOutlined />
          Laboratory
        </span>
      ),
      children: <NotificationLaboratory />,
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Vorlagen, Versand und Zustellung verwalten.</Typography.Text>

        <QueueStatusBadge {...queueStatus} />
      </div>

      <Tabs
        items={tabItems}
        defaultActiveKey="types"
        onChange={key => {
          if (key === 'analytics') fetchAnalytics();
          if (key === 'logs') {
            logsInitializedRef.current = true;
            fetchLogs();
          }
        }}
      />

      <NotificationTypeEditorModal
        visible={editorVisible}
        notificationType={editingType}
        onClose={() => {
          setEditorVisible(false);
          setEditingType(null);
        }}
        onSuccess={() => {
          setEditorVisible(false);
          setEditingType(null);
          fetchTypes();
        }}
      />

      <UserDetailModal
        userId={selectedUserId}
        visible={detailModalVisible}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedUserId(null);
        }}
      />

      <Drawer
        title="Notification Log Details"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedLog(null);
        }}
        width={640}
      >
        {selectedLog && (
          <>
            <Descriptions
              title="Notification Info"
              column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
              bordered
              size="small"
            >
              <Descriptions.Item label="Type" span="filled">
                <Text code>{selectedLog.notificationTypeCode}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Language">{selectedLog.language || '-'}</Descriptions.Item>
              <Descriptions.Item label="Triggered By">
                <Tag color={selectedLog.triggeredBy === 'STAFF' ? 'blue' : 'default'}>
                  {selectedLog.triggeredBy || 'SYSTEM'}
                </Tag>
              </Descriptions.Item>
              {selectedLog.staffId && (
                <Descriptions.Item label="Staff ID" span="filled">
                  <Text copyable>{selectedLog.staffId}</Text>
                </Descriptions.Item>
              )}
              {selectedLog.eventId && (
                <Descriptions.Item label="Event ID" span="filled">
                  <Text copyable>{selectedLog.eventId}</Text>
                </Descriptions.Item>
              )}
              {selectedLog.repeatSequence != null && (
                <Descriptions.Item label="Repeat #" span="filled">
                  {selectedLog.repeatSequence}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Descriptions title="Content" column={1} bordered size="small">
              <Descriptions.Item label="Title">{selectedLog.title || '-'}</Descriptions.Item>
              <Descriptions.Item label="Body">
                <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedLog.body || '-'}</Text>
              </Descriptions.Item>
              {selectedLog.deepLink && (
                <Descriptions.Item label="Deep Link">
                  <Text code copyable>
                    {selectedLog.deepLink}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Descriptions title="Delivery" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
              <Descriptions.Item label="Final Channel">
                {selectedLog.finalChannel ? (
                  <Tag
                    color={
                      {
                        [NotificationChannel.PUSH]: 'purple',
                        [NotificationChannel.EMAIL]: 'cyan',
                        [NotificationChannel.SMS]: 'orange',
                        [NotificationChannel.WHATSAPP]: 'green',
                        [NotificationChannel.IN_APP_BANNER]: 'magenta',
                      }[selectedLog.finalChannel] || 'default'
                    }
                  >
                    {selectedLog.finalChannel}
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Final Status">
                <Tag
                  color={
                    (
                      {
                        DELIVERED: 'green',
                        FAILED: 'red',
                        CANCELLED: 'orange',
                        OPTED_OUT: 'default',
                      } as Record<string, string>
                    )[selectedLog.finalStatus || ''] || 'default'
                  }
                >
                  {selectedLog.finalStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {selectedLog.createdAt ? formatDateTime(new Date(selectedLog.createdAt).toISOString()) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {selectedLog.updatedAt ? formatDateTime(new Date(selectedLog.updatedAt).toISOString()) : '-'}
              </Descriptions.Item>
              {selectedLog.error && (
                <Descriptions.Item label="Error" span="filled">
                  <Text type="danger">{selectedLog.error}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <Title level={5}>Channel Attempts</Title>
            <Table
              scroll={{ x: 'max-content' }}
              dataSource={selectedLog.channelAttempts || []}
              rowKey={(_, i) => String(i)}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Channel',
                  dataIndex: 'channel',
                  render: (ch: string) => (
                    <Tag
                      color={
                        {
                          [NotificationChannel.PUSH]: 'purple',
                          [NotificationChannel.EMAIL]: 'cyan',
                          [NotificationChannel.SMS]: 'orange',
                          [NotificationChannel.WHATSAPP]: 'green',
                          [NotificationChannel.IN_APP_BANNER]: 'magenta',
                        }[ch] || 'default'
                      }
                    >
                      {ch}
                    </Tag>
                  ),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  render: (s: string) => (
                    <Tag color={s === 'SUCCESS' ? 'green' : s === 'FAILED' ? 'red' : 'default'}>{s}</Tag>
                  ),
                },
                {
                  title: 'Attempted',
                  dataIndex: 'attemptedAt',
                  render: (d: string) => (d ? formatDateTime(new Date(d).toISOString()) : '-'),
                },
                {
                  title: 'Delivered',
                  dataIndex: 'deliveredAt',
                  render: (d: string) => (d ? formatDateTime(new Date(d).toISOString()) : '-'),
                },
                {
                  title: 'Error',
                  dataIndex: 'error',
                  ellipsis: true,
                  render: (e: string) => (e ? <Text type="danger">{e}</Text> : '-'),
                },
                {
                  title: 'Provider ID',
                  dataIndex: 'providerMessageId',
                  ellipsis: true,
                  render: (id: string) => (id ? <Text copyable={{ text: id }}>{id.slice(0, 16)}...</Text> : '-'),
                },
              ]}
            />

            <Divider />

            <Descriptions title="User" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
              <Descriptions.Item label="User ID" span="filled">
                {selectedLog.userId ? (
                  <Space wrap>
                    <Text copyable>{selectedLog.userId}</Text>
                    <Button
                      type="link"
                      size="small"
                      icon={<UserOutlined />}
                      onClick={() => {
                        if (selectedLog.userId) handleViewDetails(selectedLog.userId);
                      }}
                    >
                      View
                    </Button>
                  </Space>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Name">
                {selectedLog.user
                  ? `${selectedLog.user.firstName || ''} ${selectedLog.user.lastName || ''}`.trim() || '-'
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedLog.user?.phoneNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email" span="filled">
                {selectedLog.user?.email ? (
                  <Space wrap>
                    <MailOutlined />
                    <Text copyable>{selectedLog.user.email}</Text>
                  </Space>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Title level={5}>
              <MobileOutlined /> Push Devices
            </Title>
            {pushTokensLoading ? (
              <Spin />
            ) : pushTokens.length === 0 ? (
              <Text type="secondary">{noDevicesDescription(pushPermission)}</Text>
            ) : (
              <Table
                scroll={{ x: 'max-content' }}
                dataSource={pushTokens}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Device',
                    key: 'device',
                    render: (_, record) => (
                      <Space wrap>
                        {record.deviceType === 'ios' ? <AppleOutlined /> : <AndroidOutlined />}
                        <Text>{record.deviceName || record.deviceType}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'notificationStatus',
                    render: (s: string) => (
                      <Tag
                        color={
                          s === 'WORKING'
                            ? 'green'
                            : s === 'FAILED'
                              ? 'red'
                              : s === 'PERMISSION_DENIED'
                                ? 'orange'
                                : 'default'
                        }
                      >
                        {s}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Permission',
                    dataIndex: 'permissionStatus',
                    render: (s: string) => (
                      <Tag color={s === 'granted' ? 'green' : s === 'denied' ? 'red' : 'default'}>{s}</Tag>
                    ),
                  },
                  {
                    title: 'Last Delivery',
                    key: 'lastDelivery',
                    render: (_, record) => (
                      <Space direction="vertical" size={0}>
                        {record.lastDeliveryStatus && (
                          <Tag color={record.lastDeliveryStatus === 'success' ? 'green' : 'red'}>
                            {record.lastDeliveryStatus}
                          </Tag>
                        )}
                        {record.lastDeliveryAt && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatDateTime(new Date(record.lastDeliveryAt).toISOString())}
                          </Text>
                        )}
                        {record.lastDeliveryError && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {record.lastDeliveryError}
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: 'Failures',
                    dataIndex: 'failureCount',
                    width: 70,
                    render: (count: number) => <Text type={count > 0 ? 'danger' : undefined}>{count}</Text>,
                  },
                ]}
              />
            )}

            <Divider />

            <Descriptions
              title="Read / Delete Status"
              column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
              bordered
              size="small"
            >
              <Descriptions.Item label="Read">
                {selectedLog.isRead ? (
                  <Space wrap>
                    <EyeOutlined style={{ color: '#52c41a' }} />
                    <Text>Yes</Text>
                  </Space>
                ) : (
                  <Space wrap>
                    <EyeInvisibleOutlined />
                    <Text type="secondary">No</Text>
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Read At">
                {selectedLog.readAt ? formatDateTime(new Date(selectedLog.readAt).toISOString()) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Deleted">
                {selectedLog.isDeleted ? (
                  <Space wrap>
                    <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    <Text type="danger">Yes</Text>
                  </Space>
                ) : (
                  <Text type="secondary">No</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Deleted At">
                {selectedLog.deletedAt ? formatDateTime(new Date(selectedLog.deletedAt).toISOString()) : '-'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>
    </>
  );
}
